import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import profileStore from "./profileStore";
import templateStore from "./templateStore";
import reportStore from "./reportStore";
import moment from "moment";

const PAYMENT_PAGE_LIMIT = 20;

const normalizePaymentsResponse = (response, requestedPage) => {
    const unwrapped = response?.response || response;
    const payload = (
        unwrapped &&
        !Array.isArray(unwrapped) &&
        unwrapped.data &&
        !Array.isArray(unwrapped.data) &&
        (Array.isArray(unwrapped.data.data) || unwrapped.data.pagination || unwrapped.data.payments)
    ) ? unwrapped.data : unwrapped;

    const payments = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data)
            ? payload.data
            : (Array.isArray(payload?.payments) ? payload.payments : []));
    const pagination = payload?.pagination || {};
    const page = Number(pagination.page) || requestedPage;
    const limit = Number(pagination.limit) || PAYMENT_PAGE_LIMIT;
    const total = pagination.total != null
        ? Number(pagination.total)
        : (payload?.total != null ? Number(payload.total) : payments.length);
    const totalPages = pagination.totalPages != null
        ? Number(pagination.totalPages)
        : Math.max(1, Math.ceil(total / limit));

    return {
        payments,
        page,
        total: Number.isFinite(total) ? total : payments.length,
        totalPages: Number.isFinite(totalPages) ? totalPages : 1,
        hasNext: pagination.hasNext
    };
};

class SuperLedgerPaymentStore {
    @observable payerPayments = [];
    @observable payeePayments = [];
    @observable payerPage = 1;
    @observable payeePage = 1;
    @observable payerHasMore = false;
    @observable payeeHasMore = false;
    @observable isLoading = false;
    @observable payerTotal = 0;
    @observable payeeTotal = 0;

    @observable filter = {
        status: undefined,
        sourceType: undefined
    }

    @action
    resetFilter() {
        this.filter = {
            status: undefined,
            sourceType: undefined
        }
    }

    @action
    setFilterAttr(key, value) {
        this.filter[key] = value;
    }

    @action
    resetPayerPage() {
        this.payerPage = 1;
        this.payerPayments = [];
        this.payerHasMore = false;
        this.payerTotal = 0;
    }

    @action
    resetPayeePage() {
        this.payeePage = 1;
        this.payeePayments = [];
        this.payeeHasMore = false;
        this.payeeTotal = 0;
    }

    @action
    resetAll() {
        this.resetPayerPage();
        this.resetPayeePage();
        this.resetFilter();
    }

    @action
    getPaymentsByPayerId(profileId, append = false, requestedPage = this.payerPage) {
        const startDate = reportStore.startDate ? moment(reportStore.startDate).format("YYYY-MM-DD") : "";
        const endDate = reportStore.endDate ? moment(reportStore.endDate).format("YYYY-MM-DD") : "";

        this.isLoading = true;
        templateStore.showLoadingScreen();

        const params = {
            startDate,
            endDate,
            status: this.filter.status || "",
            sourceType: this.filter.sourceType || "",
            page: requestedPage,
            limit: PAYMENT_PAGE_LIMIT,
            venueId: reportStore.venueId || undefined,
            organisationId: reportStore.organisationId || undefined,
            stateId: reportStore.stateId || undefined,
            regionId: reportStore.regionId || undefined,
        };

        return agent.SuperLedgerPayments.getPaymentsByPayerId(profileId, params)
            .then(
                action(response => {
                    const result = normalizePaymentsResponse(response, requestedPage);
                    const payments = result.payments;
                    
                    if (append) {
                        this.payerPayments = [...this.payerPayments, ...payments];
                    } else {
                        this.payerPayments = payments;
                    }
                    this.payerPage = result.page;
                    this.payerTotal = result.total;
                    this.payerHasMore = typeof result.hasNext === "boolean"
                        ? result.hasNext
                        : (result.page < result.totalPages || this.payerPayments.length < result.total);
                    return response;
                })
            )
            .finally(() => {
                templateStore.hideLoadingScreen();
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action
    getPaymentsByPayeeId(profileId, append = false, requestedPage = this.payeePage) {
        const startDate = reportStore.startDate ? moment(reportStore.startDate).format("YYYY-MM-DD") : "";
        const endDate = reportStore.endDate ? moment(reportStore.endDate).format("YYYY-MM-DD") : "";

        this.isLoading = true;
        templateStore.showLoadingScreen();

        const params = {
            startDate,
            endDate,
            status: this.filter.status || "",
            sourceType: this.filter.sourceType || "",
            page: requestedPage,
            limit: PAYMENT_PAGE_LIMIT,
            venueId: reportStore.venueId || undefined,
            organisationId: reportStore.organisationId || undefined,
            stateId: reportStore.stateId || undefined,
            regionId: reportStore.regionId || undefined,
        };

        return agent.SuperLedgerPayments.getPaymentsByPayeeId(profileId, params)
            .then(
                action(response => {
                    const result = normalizePaymentsResponse(response, requestedPage);
                    const payments = result.payments;
                    
                    if (append) {
                        this.payeePayments = [...this.payeePayments, ...payments];
                    } else {
                        this.payeePayments = payments;
                    }
                    this.payeePage = result.page;
                    this.payeeTotal = result.total;
                    this.payeeHasMore = typeof result.hasNext === "boolean"
                        ? result.hasNext
                        : (result.page < result.totalPages || this.payeePayments.length < result.total);
                    return response;
                })
            )
            .finally(() => {
                templateStore.hideLoadingScreen();
                runInAction(() => {
                    this.isLoading = false;
                });
            });
    }

    @action
    loadMorePayerPayments(profileId) {
        if (this.isLoading || !this.payerHasMore) {
            return Promise.resolve();
        }
        return this.getPaymentsByPayerId(profileId, true, this.payerPage + 1);
    }

    @action
    loadMorePayeePayments(profileId) {
        if (this.isLoading || !this.payeeHasMore) {
            return Promise.resolve();
        }
        return this.getPaymentsByPayeeId(profileId, true, this.payeePage + 1);
    }

    @observable selectedPayment = null;
    @observable selectedPaymentLedgers = [];
    @observable isLoadingPayment = false;

    @action
    getPaymentById(paymentId, profileId) {
        this.isLoadingPayment = true;
        templateStore.showLoadingScreen();

        return agent.SuperLedgerPayments.getPaymentById(paymentId, profileId)
            .then(
                action(response => {
                    const data = response?.response || response;
                    this.selectedPayment = data;
                    this.selectedPaymentLedgers = data?.ledgers || data?.superLedgers || [];
                    return response;
                })
            )
            .finally(() => {
                templateStore.hideLoadingScreen();
                runInAction(() => {
                    this.isLoadingPayment = false;
                });
            });
    }

    @action
    deletePayment(paymentId) {
        this.isLoadingPayment = true;
        templateStore.showLoadingScreen();

        return agent.SuperLedgerPayments.deletePayment(paymentId)
            .then(
                action(response => {
                    const hadPayerPayment = this.payerPayments.some(p => p.id === paymentId);
                    const hadPayeePayment = this.payeePayments.some(p => p.id === paymentId);
                    
                    this.payerPayments = this.payerPayments.filter(p => p.id !== paymentId);
                    this.payeePayments = this.payeePayments.filter(p => p.id !== paymentId);
                    
                    if (hadPayerPayment) {
                        this.payerTotal = Math.max(0, this.payerTotal - 1);
                        this.payerHasMore = this.payerPayments.length < this.payerTotal;
                    }
                    if (hadPayeePayment) {
                        this.payeeTotal = Math.max(0, this.payeeTotal - 1);
                        this.payeeHasMore = this.payeePayments.length < this.payeeTotal;
                    }
                    
                    this.selectedPayment = null;
                    this.selectedPaymentLedgers = [];
                    return response;
                })
            )
            .finally(() => {
                templateStore.hideLoadingScreen();
                runInAction(() => {
                    this.isLoadingPayment = false;
                });
            });
    }

    @action
    clearSelectedPayment() {
        this.selectedPayment = null;
        this.selectedPaymentLedgers = [];
    }

    @action
    excludeLedgersFromPayment(paymentId, ledgerIds, profileId) {
        this.isLoadingPayment = true;
        templateStore.showLoadingScreen();

        return agent.SuperLedgerPayments.excludeLedgersFromPayment(paymentId, ledgerIds)
            .then(
                action(response => {
                    return this.getPaymentById(paymentId, profileId);
                })
            )
            .catch(error => {
                templateStore.hideLoadingScreen();
                runInAction(() => {
                    this.isLoadingPayment = false;
                });
                throw error;
            });
    }

    @action
    removeLedgersFromPayment(paymentId, paymentLedgerIds) {
        this.isLoadingPayment = true;
        templateStore.showLoadingScreen();

        return agent.SuperLedgerPayments.removeLedgersFromPayment(paymentId, paymentLedgerIds)
            .then(
                action(response => {
                    const data = response?.response || response;
                    
                    if (data?.success) {
                        if (this.selectedPayment) {
                            this.selectedPayment.totalAmount = data.newTotalAmount;
                        }
                        
                        // Filter out removed ledgers by their payment ledger ID
                        this.selectedPaymentLedgers = this.selectedPaymentLedgers.filter(
                            ledger => !paymentLedgerIds.includes(ledger.id)
                        );
                        
                        const currentProfileId = profileStore.currentProfile?.id;
                        if (currentProfileId) {
                            const paymentIndex = this.payerPayments.findIndex(p => p.id === paymentId);
                            if (paymentIndex !== -1) {
                                this.payerPayments[paymentIndex] = {
                                    ...this.payerPayments[paymentIndex],
                                    totalAmount: data.newTotalAmount,
                                    ledgerCount: data.remainingLedgerCount
                                };
                            }
                        }
                    }
                    
                    return data;
                })
            )
            .finally(() => {
                templateStore.hideLoadingScreen();
                runInAction(() => {
                    this.isLoadingPayment = false;
                });
            });
    }
}

const superLedgerPaymentStore = new SuperLedgerPaymentStore();
export default superLedgerPaymentStore;
